import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SwiperSlide } from 'swiper/react';

import ExpandableSection from '@components/animations/ExpandableSection';
import styles from '@components/Article.module.css';
import SecondaryButton from '@components/buttons/SecondaryButton';
import ArticleCard from '@components/cards/ArticleCard';
import MyStoryLeadForm from '@components/forms/MyStoryLeadForm';
import CardSlides from '@components/layout/CardSlides';
import Modal from '@components/Modal';
import Icon from '@components/text/Icon';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading1 from '@components/typography/Heading1';
import Heading2 from '@components/typography/Heading2';
import Hyperlink from '@components/typography/Hyperlink';
import Video from '@components/Video';
import useSubscribeToJourney from '@hooks/subscriptions/useSubscribeToJourney';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  ArticleContentTypeEnum,
  ArticleFragment as ArticleType,
  GetJourneyWithExpertDocument,
  GetJourneyWithExpertQuery,
  GetJourneyWithExpertQueryVariables
} from '@typing/Generated';
import { articleHasVideo } from '@utils/articleUtils';
import { imageUrl } from '@utils/imageUtils';

type Props = {
  article: ArticleType;
  journeyId: string | null;
  refetch: () => void;
  videoPlayedAppEventCreate?: () => void;
};

const Article = ({ article, journeyId, refetch, videoPlayedAppEventCreate }: Props) => {
  const intl = useIntl();
  const intervalRef = useRef<NodeJS.Timeout | undefined>();
  const transcriptOpener = useOpener();
  const myStoryLeadOpener = useOpener();
  const myStoryLeadSuccessOpener = useOpener();
  const [played, setPlayed] = useState(false);

  const [getJourney, getJourneyQuery] = useLazyQuery<GetJourneyWithExpertQuery, GetJourneyWithExpertQueryVariables>(
    GetJourneyWithExpertDocument
  );

  useSubscribeToJourney(journeyId, () => {
    getJourneyQuery.refetch();
  });

  useEffect(() => {
    if (journeyId) {
      getJourney({
        variables: { id: journeyId }
      });
    }
  }, [getJourney, journeyId]);

  const onVideoPlay = useCallback(() => {
    if (!played && videoPlayedAppEventCreate) {
      videoPlayedAppEventCreate();
      setPlayed(true);
    }
  }, [played, videoPlayedAppEventCreate]);

  useEffect(() => {
    if (article.photoScanning && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        refetch();
      }, 5000);
    } else if (!article.photoScanning && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [article, refetch]);

  const handleMyStoryLeadSuccess = useCallback(() => {
    myStoryLeadOpener.close();
    myStoryLeadSuccessOpener.open();
  }, [myStoryLeadOpener, myStoryLeadSuccessOpener]);

  const sourcesOpener = useOpener();

  const sanitizedText = useSanitizeHtml(article.text, journeyId, article);
  const sanitizedTranscript = useSanitizeHtml(article.transcript, journeyId, article);

  return (
    <>
      <div className={styles.articleGrid}>
        <div>
          <div className={styles.title}>
            <Heading1>{article.title}</Heading1>
          </div>

          <div className={styles.imgContainer}>
            {articleHasVideo(article) && article.videoUrl ? (
              <div className={styles.video}>
                <Video onPlay={onVideoPlay} posterUrl={article.photoUrl} videoUrl={article.videoUrl} />
              </div>
            ) : article.photoUrl ? (
              <img
                alt={intl.formatMessage({ id: 'accessibility.altTags.article' })}
                className={styles.image}
                src={article.photoUrl}
              />
            ) : (
              <></>
            )}
          </div>

          {article.contentType === ArticleContentTypeEnum.text && <div>{sanitizedText}</div>}
          {articleHasVideo(article) && (
            <div className={transcriptOpener.isOpen ? '' : 'ion-hide'}>{sanitizedTranscript}</div>
          )}
        </div>
        <div>
          <div className={styles.quote}>
            <Heading2 bold={false} color="neutral500">
              {article.quote}
            </Heading2>
          </div>
          {article.sources.length > 0 && (
            <div>
              <div className={styles.sourcesOpener} onClick={sourcesOpener.toggle}>
                <div className={styles.sourcesAndIcon}>
                  <Icon ariaLabelKey="accessibility.ariaLabels.sources" icon="book" />
                  <BodyCopyBold color="primary700">
                    <FormattedMessage id="models.article.sources" />
                  </BodyCopyBold>
                </div>
                <Icon
                  ariaLabelKey="accessibility.ariaLabels.toggle"
                  icon={sourcesOpener.isOpen ? 'chevronUp' : 'chevronDown'}
                />
              </div>
              <div>
                <ExpandableSection isOpen={sourcesOpener.isOpen} key="article-sources">
                  <div className={styles.articleSources}>
                    {article.sources.map(source => (
                      <div key={source.value}>
                        <Hyperlink href={source.value} rel="noopener noreferrer" target="_blank">
                          {source.label}
                        </Hyperlink>
                      </div>
                    ))}
                  </div>
                </ExpandableSection>
              </div>
            </div>
          )}
        </div>
      </div>

      {journeyId && article.relatedArticles.length > 0 && (
        <div className={styles.relatedContent}>
          <div className={styles.relatedContentHeading}>
            <Heading2 color="primary700">
              <FormattedMessage id="pages.article.relatedContent" />
            </Heading2>
          </div>

          <CardSlides itemCount={article.relatedArticles.length}>
            {article.relatedArticles.map(article => (
              <SwiperSlide key={article.id}>
                <ArticleCard article={article} journeyId={journeyId} webinarSmall />
              </SwiperSlide>
            ))}
          </CardSlides>
        </div>
      )}

      {article.contentType === ArticleContentTypeEnum.my_story && journeyId && (
        <>
          <Modal
            opener={myStoryLeadOpener}
            title={intl.formatMessage({
              id: 'pages.community.shareYourStory'
            })}
          >
            {({ modalDisplayed }) => (
              <>
                <div className={styles.helpOthers}>
                  <FormattedMessage id="pages.community.helpOthers" />
                </div>
                <MyStoryLeadForm
                  autofocus={modalDisplayed}
                  journeyId={journeyId}
                  onCancel={myStoryLeadOpener.close}
                  onSuccess={handleMyStoryLeadSuccess}
                />
              </>
            )}
          </Modal>
          <Modal opener={myStoryLeadSuccessOpener}>
            {() => (
              <div className={styles.center}>
                <img
                  alt={intl.formatMessage({ id: 'accessibility.altTags.shareStory' })}
                  className={styles.storyImg}
                  src={imageUrl('communityShareStory')}
                />
                <div className={styles.hugeText}>
                  <FormattedMessage id="pages.community.thanksForSharing" />
                </div>
                <div className={styles.smallText}>
                  <FormattedMessage id="pages.community.someoneFromGrayce" />
                </div>
                <div className={styles.close}>
                  <SecondaryButton expand="block" onClick={myStoryLeadSuccessOpener.close} textKey="dictionary.close" />
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </>
  );
};

export default Article;
